import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Shield, Plus, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const PERM_FIELDS = ["view", "create", "edit", "delete", "print"];

const leavesOf = (n) => (n.children ? n.children.flatMap(leavesOf) : [n.key]);
const flattenTree = (nodes, depth = 0) =>
  nodes.flatMap((n) => [{ ...n, depth }, ...(n.children ? flattenTree(n.children, depth + 1) : [])]);
const blankPerms = (tree) => {
  const p = {};
  tree.flatMap(leavesOf).forEach((k) => { p[k] = Object.fromEntries(PERM_FIELDS.map((f) => [f, false])); });
  return p;
};

function MenuPermSection({ nodes, perms, setPerms }) {
  const allLeaves = useMemo(() => nodes.flatMap(leavesOf), [nodes]);
  const rows = useMemo(() => flattenTree(nodes), [nodes]);

  const setKeys = (keys, f, v) =>
    setPerms((p) => {
      const next = { ...p };
      keys.forEach((k) => { next[k] = { ...(next[k] || {}), [f]: v }; });
      return next;
    });
  const setRowAll = (keys, v) =>
    setPerms((p) => {
      const next = { ...p };
      keys.forEach((k) => { next[k] = Object.fromEntries(PERM_FIELDS.map((f) => [f, v])); });
      return next;
    });
  const setAll = (v) => setRowAll(allLeaves, v);

  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid="menu-perm-section">
      <div className="flex items-center justify-between gap-2 p-3 border-b border-border bg-secondary/40 flex-wrap">
        <div>
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary" data-testid="menu-perm-title">Menu and Sub-Menu Permissions</h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">Checking a parent checks all its sub-menus. Unchecking a sub-menu unchecks the parent.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest h-7"
            data-testid="menu-perm-select-all" onClick={() => setAll(true)}>Select All</Button>
          <Button type="button" size="sm" variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest h-7"
            data-testid="menu-perm-deselect-all" onClick={() => setAll(false)}>Deselect All</Button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <tr>
            <th className="text-left p-2 pl-3">Menu / Sub-Menu</th>
            <th className="text-center p-2 w-12">All</th>
            {PERM_FIELDS.map((f) => <th key={f} className="text-center p-2 w-14 capitalize">{f}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((n) => {
            const keys = leavesOf(n);
            const parent = !!n.children;
            const allCount = keys.filter((k) => PERM_FIELDS.every((f) => perms[k]?.[f])).length;
            const allChecked = allCount === keys.length;
            const allInd = allCount > 0 && !allChecked;
            return (
              <tr key={n.key} data-testid={`menu-perm-row-${n.key}`}
                className={`border-t border-border ${parent ? "bg-secondary/30" : ""}`}>
                <td className={parent ? "p-2 font-bold uppercase tracking-widest text-[10px]" : "p-2 font-medium text-xs"}
                  style={{ paddingLeft: 12 + n.depth * 22 }}>{n.label}</td>
                <td className="p-2 text-center">
                  <Checkbox
                    data-testid={`menu-perm-${n.key}-all`}
                    checked={allInd ? "indeterminate" : allChecked}
                    onCheckedChange={(v) => setRowAll(keys, v === true)} />
                </td>
                {PERM_FIELDS.map((f) => {
                  const count = keys.filter((k) => perms[k]?.[f]).length;
                  const checked = count === keys.length;
                  const ind = count > 0 && !checked;
                  return (
                    <td key={f} className="p-2 text-center">
                      <Checkbox
                        data-testid={`menu-perm-${n.key}-${f}`}
                        checked={ind ? "indeterminate" : checked}
                        onCheckedChange={(v) => setKeys(keys, f, v === true)} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const emptyForm = { name: "", description: "", tree_menus: {} };

export default function RolesMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [menuTree, setMenuTree] = useState([]);
  const [f, setF] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () =>
    Promise.all([api.get("/roles"), api.get("/admin/admin-permission-tree")])
      .then(([r, t]) => { setList(r.data); setMenuTree(t.data.menu_tree || []); })
      .catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const setPerms = (updater) =>
    setF((prev) => ({ ...prev, tree_menus: typeof updater === "function" ? updater(prev.tree_menus) : updater }));

  const openNew = () => {
    setF({ ...emptyForm, tree_menus: blankPerms(menuTree) });
    setEditId(null); setOpen(true);
  };
  const edit = (r) => {
    setF({ name: r.name, description: r.description || "",
           tree_menus: { ...blankPerms(menuTree), ...(r.tree_menus || {}) } });
    setEditId(r.id); setOpen(true);
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Role name required"); return; }
    setBusy(true);
    try {
      const payload = { name: f.name, description: f.description, tree_menus: f.tree_menus };
      if (editId) await api.patch(`/roles/${editId}`, payload);
      else await api.post("/roles", payload);
      toast.success(editId ? "Updated" : "Role created");
      setF(emptyForm); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const [delTarget, setDelTarget] = useState(null);
  const del = async () => {
    if (!delTarget) return;
    setBusy(true);
    try {
      await api.delete(`/roles/${delTarget.id}`);
      toast.success(`Role "${delTarget.name}" deleted`);
      setDelTarget(null); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-8" data-testid="roles-master-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Shield className="w-3 h-3"/> Owner only · Built-in + custom roles</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Role Master</h1>
        </div>
        <Button data-testid="add-role-btn" onClick={openNew}
          className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
          <Plus className="w-4 h-4 mr-1"/> New Custom Role
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyForm); setEditId(null); } }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="role-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Role</DialogTitle>
            <DialogDescription className="text-xs">Role name, description, and menu permission matrix — saved together.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" onKeyDownCapture={handleSimpleFormNav}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Role Name</Label>
                <Input data-testid="r-name" value={f.name}
                  onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))}
                  placeholder="e.g. accountant" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
                {!!editId && <p className="text-[10px] text-muted-foreground mt-1">Saved lowercase with underscores. Renaming is blocked while users are assigned; built-in names are fixed.</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
                <Textarea data-testid="r-desc" value={f.description}
                  onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))}
                  className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
              </div>
            </div>

            <MenuPermSection nodes={menuTree} perms={f.tree_menus} setPerms={setPerms} />

            <Button data-testid="save-role-btn" onClick={save} disabled={!f.name || busy}
              className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
              {busy ? "Saving…" : editId ? "Update Role" : "Create Role"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent className="rounded-sm bg-card border-border" data-testid="role-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase" data-testid="role-delete-title">
              {delTarget?.user_count > 0 ? "Role In Use" : "Delete Role?"}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="role-delete-message">
              {delTarget?.user_count > 0
                ? `"${delTarget.name}" is assigned to ${delTarget.user_count} user(s). Reassign those users to another role before deleting this one.`
                : `This permanently removes "${delTarget?.name}" and its permission settings. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-delete-cancel" className="rounded-sm uppercase tracking-[0.15em] text-xs">
              {delTarget?.user_count > 0 ? "Got It" : "Cancel"}
            </AlertDialogCancel>
            {!(delTarget?.user_count > 0) &&
              <AlertDialogAction data-testid="role-delete-confirm" onClick={del} disabled={busy}
                className="rounded-sm uppercase tracking-[0.15em] text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {busy ? "Deleting…" : "Delete Permanently"}
              </AlertDialogAction>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Role</th><th className="text-left p-3">Type</th>
              <th className="text-left p-3">Description</th><th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(r => (
              <tr key={r.id} data-testid={`role-row-${r.name}`} className="border-t border-border">
                <td className="p-3 font-mono text-xs uppercase tracking-wider text-primary">{r.name}</td>
                <td className="p-3 text-xs">{r.is_system ? <span className="flex items-center gap-1 text-muted-foreground"><Lock className="w-3 h-3"/> Built-in</span> : <span className="text-primary">Custom</span>}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.description || "-"}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`edit-role-${r.name}`} size="sm" variant="ghost" onClick={() => edit(r)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-role-${r.name}`} size="sm" variant="ghost" onClick={() => setDelTarget(r)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
